import axios from 'axios';
import { getAuth } from 'firebase/auth';

// API Configuration
// Base URL resolution — first non-empty wins:
//
//   1. window.__RIALTY_API_BASE_URL__ (runtime override) — the mobile app's
//      WebView host (see Mob's ColoradoFormEditScreen.tsx) injects it before
//      this module evaluates. The build-time env var baked for a human's own
//      browser (e.g. localhost:8088) means something different inside an
//      Android emulator's WebView (localhost = the emulator itself, not the
//      host machine), so the mobile client hands over its own correct gateway
//      address (10.0.2.2:8088 for emulator, real domain for a device).
//   2. process.env.REACT_APP_API_BASE_URL (build-time env var) — CRA reads
//      from .env.local / .env.development / .env.production during `npm start`
//      and `npm run build`. Set REACT_APP_API_BASE_URL=http://localhost:8088
//      in .env.local for a local-gateway dev loop without touching this file.
//   3. Local dev auto-detect — when neither override is set and the browser
//      is on localhost / 127.0.0.1 / 0.0.0.0, default to the local gateway
//      on port 8088. This makes a fresh clone Just Work with
//      `docker compose up` without needing a .env file, while deployed
//      builds (hostname !== localhost) keep hitting the remote API.
//   4. https://api-dev.rialty.ai — final fallback for deployed builds that
//      shipped without REACT_APP_API_BASE_URL baked in.
const isLocalHost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location?.hostname);

export const API_BASE_URL =
  (typeof window !== 'undefined' && window.__RIALTY_API_BASE_URL__) ||
  process.env.REACT_APP_API_BASE_URL ||
  (isLocalHost ? 'http://localhost:8088' : 'https://api-dev.rialty.ai');

// CRM calls can be pointed at a different gateway than everything else. Used to try the CRM
// against a local gateway (REACT_APP_CRM_API_BASE_URL=http://localhost:8088) while the rest of
// the app keeps using the shared environment. Unset in every deployed build, where it is the
// same as API_BASE_URL.
export const CRM_API_BASE_URL = process.env.REACT_APP_CRM_API_BASE_URL || API_BASE_URL;

// Same idea for Ria's chat: lets a developer try a locally running Ria (and its CRM tools) through a local
// gateway while the rest of the app keeps using the shared environment. Unset in every deployed build.
export const RIA_API_BASE_URL = process.env.REACT_APP_RIA_API_BASE_URL || API_BASE_URL;

// Shared axios instance with 30-second default timeout
export const apiClient = axios.create({
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

// Helper to check if a JWT token is expired
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const handleSessionExpired = async () => {
  try {
    const auth = getAuth();
    await auth.signOut();
  } catch (e) {
    console.error('Error signing out:', e);
  }
  localStorage.removeItem('user');
  localStorage.removeItem('firebaseToken');
  window.location.href = '/login';
};

// =============================================================================
// Opt-in per-request retry (network-level)
// =============================================================================
// Disabled by default — only requests that pass a `retry` config get this
// behavior, so the rest of the app is unaffected. Callers opt in via:
//   apiClient.post(url, data, { retry: { retries: 10, retryOn: isNetworkErrorOnly, retryDelayMs: 1000, buildData: () => freshBody } })

// Default predicate: fires on timeout, dropped connection (no response), and
// 502/503/504 upstream errors. 4xx (including 401/429) are never retried here.
export const isRetryableError = (error) => {
  if (axios.isCancel(error)) return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;
  if (!error.response) return true;
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
};

// Network-only predicate: fires only for a pure network error (request never
// got a response at all — dropped socket, DNS failure, offline). Deliberately
// excludes ECONNABORTED (client-side timeout) — for a long-running upload the
// bytes likely already reached the server, so retrying would re-run whatever
// expensive job the server started rather than recover a lost request.
export const isNetworkErrorOnly = (error) => {
  if (axios.isCancel(error)) return false;
  if (error.code === 'ECONNABORTED') return false;
  return !error.response;
};

// Exponential backoff capped at 30s so a caller requesting many retries (e.g.
// 10 attempts on a multipart upload) doesn't tail out to minutes-long delays
// that blow past the per-attempt timeout budget.
const RETRY_BACKOFF_MAX_MS = 30000;
const retryBackoffMs = (attempt, baseDelayMs) =>
  Math.min(baseDelayMs * Math.pow(2, attempt), RETRY_BACKOFF_MAX_MS) + Math.floor(Math.random() * 250);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const maybeRetryRequest = async (error) => {
  const config = error.config;
  const retryCfg = config?.retry;
  if (!config || !retryCfg) return null;

  const maxRetries = retryCfg.retries ?? 0;
  if (maxRetries <= 0) return null;

  const predicate = retryCfg.retryOn ?? isRetryableError;
  if (!predicate(error)) return null;

  config.__retryCount = config.__retryCount ?? 0;
  if (config.__retryCount >= maxRetries) return null;
  config.__retryCount += 1;

  const delay = retryBackoffMs(config.__retryCount - 1, retryCfg.retryDelayMs ?? 1000);
  console.warn(
    `[API] retry ${config.__retryCount}/${maxRetries} in ${delay}ms — ${error.code || 'network'} ${config.url}`
  );
  await wait(delay);

  // Multipart bodies can only safely be resent if rebuilt fresh — callers
  // pass buildData() to reconstruct the FormData from its original file
  // sources rather than resending a body an HTTP client may have consumed.
  if (typeof retryCfg.buildData === 'function') {
    config.data = retryCfg.buildData();
  }

  return apiClient(config);
};

// Request interceptor - check token expiry and inject device ID on every request
apiClient.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('firebaseToken');
    if (token && isTokenExpired(token)) {
      await handleSessionExpired();
      return Promise.reject(new Error('Session expired'));
    }
    // Inject a stable browser device ID so the backend can identify the calling device
    // (mirrors the X-Device-Id header the mobile app sends via its own interceptor)
    let webDeviceId = localStorage.getItem('webDeviceId');
    if (!webDeviceId) {
      webDeviceId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('webDeviceId', webDeviceId);
    }
    config.headers['X-Device-Id'] = webDeviceId;
    // Identify the calling app. The website serves both classes and picks
    // the SP vs main dashboard based on the user's role, so the server's
    // login-completed role gate treats "web" as no-op — role-class
    // exclusivity is still enforced at role-add / registration time.
    config.headers['X-Client-App'] = 'web';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - resolve for all successful 2xx responses
apiClient.interceptors.response.use(
  (response) => {
    const status = response.status;
    if (status >= 200 && status < 300) {
      return response;
    }
    // Reject non-2xx responses so data is not rendered prematurely
    return Promise.reject(new Error(`Unexpected response status: ${status}`));
  },
  async (error) => {
    // Opt-in network-level retry — no-ops for requests that didn't pass a
    // `retry` config, so this is a no-op for every endpoint except those
    // that ask for it.
    const retried = await maybeRetryRequest(error);
    if (retried) return retried;

    // Handle timeout errors with a clear message
    if (error.code === 'ECONNABORTED') {
      console.error('API request timed out (30s limit)');
    }
    // Auto-logout on 401 — token expired or invalid
    // Skip DocuSign endpoints: their 401 means DocuSign OAuth is not connected,
    // not that the user's Rialty session is expired.
    const requestUrl = error.config?.url || '';
    const isDocuSignEndpoint = requestUrl.includes('/docusign/');
    // trusted-device-login is a public pre-auth endpoint; a 401 from it means the
    // device is not trusted (do normal login), not that the session has expired.
    const isTrustedDeviceLoginEndpoint = requestUrl.includes('/auth/trusted-device-login');
    // SP registration's "Find my company" picker calls this endpoint before the
    // invitee is signed in; a 401 means the gateway/user-service public-paths
    // whitelist hasn't rolled out yet, NOT that the user's session expired.
    // Treat as a benign "no results" and let the caller handle it (empty state).
    const isSpCompanySearchEndpoint = requestUrl.includes('/service-provider/company/search');
    // The CRM page shows its own message for a 401 ("Your session has expired"). A 401 from the CRM can also
    // be the CRM refusing a request (for example a security-context problem), which must not end the user's
    // session in the rest of the app.
    const isCrmEndpoint = requestUrl.includes('/api/v1/crm/');
    if (
      error.response?.status === 401 &&
      !isDocuSignEndpoint &&
      !isTrustedDeviceLoginEndpoint &&
      !isSpCompanySearchEndpoint &&
      !isCrmEndpoint
    ) {
      await handleSessionExpired();
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const API_ENDPOINTS = {
  verifyLicense: `${API_BASE_URL}/api/public/verify-license`,
  users: `${API_BASE_URL}/api/v1/users`,
  register: `${API_BASE_URL}/api/v1/users/register`,
  userMe: `${API_BASE_URL}/api/v1/users/me`,
  userContacts: `${API_BASE_URL}/api/v1/users/me/contacts`,
  notes: `${API_BASE_URL}/api/v1/users/me/notes`,
  notesByLink: `${API_BASE_URL}/api/v1/users/me/notes/by-link`,
  noteById: (noteId) => `${API_BASE_URL}/api/v1/users/me/notes/${noteId}`,
  noteTransactionNameRepair: (noteId) => `${API_BASE_URL}/api/v1/users/me/notes/${noteId}/transaction-name`,
  userById: (userId) => `${API_BASE_URL}/api/v1/users/${userId}`,
  userProfile: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/profile`,
  userByFirebaseUid: (firebaseUid, flag) => `${API_BASE_URL}/api/v1/users/${firebaseUid}/${flag}`,
  userRoles: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/roles`,
  setActiveRole: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/roles/active`,
  userPhoto: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/photo`,
  companyLogo: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/company-logo`,
  agentProfile: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/agent-profile`,
  tcProfile: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/tc-profile`,
  brokerProfile: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/broker-profile`,
  brokerManagerProfile: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/broker-manager-profile`,
  brokerAdminProfile: (userId) => `${API_BASE_URL}/api/v1/users/${userId}/broker-admin-profile`,
  transactions: `${API_BASE_URL}/api/v1/transactions`,
  transactionById: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}`,
  deleteTransaction: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}`,
  transactionMilestones: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/milestones`,
  applyMilestoneTemplate: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/milestones/apply-template`,
  transactionDocusignDocuments: (transactionId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents`,
  transactionDocusignDocumentsSharedWith: (transactionId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/shared-with`,
  downloadSharedDocument: (transactionId, envelopeId, documentId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/shared-with/${envelopeId}/${documentId}/download`,
  dashboard: `${API_BASE_URL}/api/v1/dashboard`,
  spDashboard: `${API_BASE_URL}/api/v1/service-providers/dashboard`,
  logout: `${API_BASE_URL}/api/v1/logout`,
  mlsProperty: (mlsId) => `${API_BASE_URL}/api/v1/mls/properties/${mlsId}`,
  checkEmail: `${API_BASE_URL}/api/v1/users/check-email`,
  inviteToTransaction: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/invite`,
  decodeInvite: (key) => `${API_BASE_URL}/api/v1/transactions/invitations/validate/${key}`,
  acceptInvite: `${API_BASE_URL}/api/v1/transactions/invitations/accept`,
  transactionParticipants: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/participants`,
  // Platform-invite a reference party (ecosystem-growth flow — no transaction
  // access granted on acceptance). Backend returns 400 with error="EMAIL_REQUIRED"
  // when the participant has no email on file; the UI prompts for email + phone
  // and retries with those values in the body.
  participantInviteToPlatform: (transactionId, participantId) =>
    `${API_BASE_URL}/api/v1/transactions/${transactionId}/participants/${participantId}/invite-to-platform`,
  deleteTransactionFile: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}`,
  // Disclosure share (agent-side, Firebase JWT)
  disclosureShares: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/disclosures`,
  disclosureShareRevoke: (transactionId, shareToken) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/disclosures/${shareToken}/revoke`,
  disclosureShareRecipients: (transactionId, shareToken) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/disclosures/${shareToken}/recipients`,
  disclosureAllRecipients: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/disclosures/recipients`,
  disclosureShareSendSms: (transactionId, shareToken) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/disclosures/${shareToken}/send-sms`,
  // Folder share (agent-side, Firebase JWT) — arbitrary Transaction Documents folder.
  // Reuses the disclosure share storage + recipient OTP flow (see backend
  // TransactionFolderShareController); only the create endpoint takes a `folder`
  // body param and authz is broadened to buy-side agents + TC.
  folderShares: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folder-shares`,
  folderSharesList: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folder-shares?folder=${encodeURIComponent(folder)}`,
  folderShareRevoke: (transactionId, shareToken) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folder-shares/${shareToken}/revoke`,
  folderShareRecipients: (transactionId, shareToken) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folder-shares/${shareToken}/recipients`,
  // Disclosure share (public — no auth for form/OTP; Bearer session token for me/documents)
  publicDisclosureMetadata: (shareToken) => `${API_BASE_URL}/api/public/disclosures/${shareToken}`,
  publicDisclosureRecipientInfo: (shareToken) => `${API_BASE_URL}/api/public/disclosures/${shareToken}/recipient-info`,
  publicDisclosureVerifyOtp: (shareToken) => `${API_BASE_URL}/api/public/disclosures/${shareToken}/verify-otp`,
  publicDisclosureMe: (shareToken) => `${API_BASE_URL}/api/public/disclosures/${shareToken}/me`,
  publicDisclosureDocuments: (shareToken) => `${API_BASE_URL}/api/public/disclosures/${shareToken}/documents`,
  publicDisclosureCombinedPdf: (shareToken) => `${API_BASE_URL}/api/public/disclosures/${shareToken}/documents/combined`,
  publicDisclosureZip: (shareToken) => `${API_BASE_URL}/api/public/disclosures/${shareToken}/documents/zip`,
  // Public offer-upload link flow (mirrors disclosures): metadata → submitter-info (OTP) → verify-otp (session JWT) → upload
  publicOfferLinkMetadata: (linkId) => `${API_BASE_URL}/api/public/offer-links/${linkId}`,
  publicOfferLinkSubmitterInfo: (linkId) => `${API_BASE_URL}/api/public/offer-links/${linkId}/submitter-info`,
  publicOfferLinkVerifyOtp: (linkId) => `${API_BASE_URL}/api/public/offer-links/${linkId}/verify-otp`,
  publicOfferLinkMe: (linkId) => `${API_BASE_URL}/api/public/offer-links/${linkId}/me`,
  publicOfferLinkUpload: (linkId) => `${API_BASE_URL}/api/public/offer-links/${linkId}/upload`,
  publicOfferLinkMySubmissions: (linkId) => `${API_BASE_URL}/api/public/offer-links/${linkId}/my-submissions`,
  transactionClients: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/clients`,
  deleteParticipant: (transactionId, participantId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/participants/${participantId}`,
  docusignFormLibraries: `${API_BASE_URL}/api/v1/docusign/form-libraries`,
  docusignFormLibraryForms: (formsLibraryId) => `${API_BASE_URL}/api/v1/docusign/form-libraries/${formsLibraryId}/forms`,
  lonewolfUserStatus: (email) => `${API_BASE_URL}/api/v1/lonewolf/users/status?email=${encodeURIComponent(email)}`,
  lonewolfFormProviderStatus: `${API_BASE_URL}/api/v1/lonewolf/form-provider-status`,
  updateLoneWolfEmail: `${API_BASE_URL}/api/v1/users/me/lonewolf-email`,
  userSmsConsent: `${API_BASE_URL}/api/v1/users/me/sms-consent`, // PATCH { consent: boolean }
  lonewolfTransactionForms: (transactionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/forms`,
  // transactionId (optional): when adding a form inside a transaction, pass it so the backend
  // runs the library lookup under the transaction's OWNING Agent's Lone Wolf account (a TC's
  // own account has no libraries). Omit for a plain self-scoped lookup.
  lonewolfLibraryByAcronym: (acronym, transactionId) => `${API_BASE_URL}/api/v1/lonewolf/libraries/by-acronym/${acronym}${transactionId ? `?transactionId=${encodeURIComponent(transactionId)}` : ''}`,
  lonewolfLibraryForms: (libraryId, transactionId) => `${API_BASE_URL}/api/v1/lonewolf/libraries/${libraryId}/forms${transactionId ? `?transactionId=${encodeURIComponent(transactionId)}` : ''}`,
  lonewolfAttachForm: (transactionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/forms/attach`,
  lonewolfDownloadForm: (transactionId, versionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/forms/${versionId}/download`,
  // Same blank form as lonewolfDownloadForm, but a TDS/SPQ comes back filled from the
  // transaction's most-recent completed wizard (transient PDF, never persisted); any other
  // form, or one with no completed wizard, is returned blank unchanged — so every form "View"
  // can safely point here. See LoneWolfController.getFilledFormPreview.
  lonewolfFilledPreview: (transactionId, versionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/forms/${versionId}/filled-preview`,
  // Signing status (Agent/TC) / signed document (all parties, finalized-only) — see
  // LoneWolfController.getSigningDocumentsForTransaction for the role-based split.
  lonewolfSigningDocuments: (transactionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/signing-documents`,
  lonewolfSignedDocument: (transactionId, signingId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/signings/${signingId}/document`,
  // Non-Colorado only — sends a Transaction Documents (GCS) custom PDF for signature via
  // Lone Wolf instead of DocuSign. See LoneWolfController.sendGcsDocumentForSignature.
  lonewolfSendGcsDocumentForSignature: (transactionId, documentId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/documents/${documentId}/send-for-signature`,
  // Send a wizard-FILLED TDS/SPQ form (Forms tab) for signature via Lone Wolf — backend fills the
  // form from the completed wizard, then runs the same create-signing flow as a custom PDF. See
  // LoneWolfController.sendFormForSignature.
  lonewolfSendFormForSignature: (transactionId, versionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/forms/${versionId}/send-for-signature`,
  // Real Lone Wolf contacts for this transaction — populates the recipient-selection dialog
  // shown before a Lone Wolf send-for-signature (same role DocuSign's transactionParticipants
  // call plays for its own recipient picker).
  lonewolfTransactionContacts: (transactionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/contacts`,
  // Bulk Draft/Sent/Signed status for every custom PDF ever sent for signature via Lone Wolf
  // on this transaction — keyed by documentId, one round trip for the whole Documents tab.
  lonewolfDocumentSignings: (transactionId) => `${API_BASE_URL}/api/v1/lonewolf/transactions/${transactionId}/documents/signings`,
  // FormService (CREC Colorado catalog + per-transaction form packages / auto-populate)
  formsCatalog: (stateCode) => `${API_BASE_URL}/api/v1/forms/catalog/${stateCode}`,
  formsCreatePackage: `${API_BASE_URL}/api/v1/forms/packages`,
  formsPackagesByTransaction: (transactionId) => `${API_BASE_URL}/api/v1/forms/packages/transaction/${transactionId}`,
  formsPackageForms: (packageId) => `${API_BASE_URL}/api/v1/forms/packages/${packageId}/forms`,
  formsAddForm: (packageId) => `${API_BASE_URL}/api/v1/forms/packages/${packageId}/forms`,
  formsDownloadPdf: (packageId, instanceId) => `${API_BASE_URL}/api/v1/forms/packages/${packageId}/forms/${instanceId}/pdf`,
  // Editable (unflattened) copy for the in-browser PDF editor — all AcroForm fields intact.
  formsEditablePdf: (packageId, instanceId) => `${API_BASE_URL}/api/v1/forms/packages/${packageId}/forms/${instanceId}/pdf?editable=true`,
  // Persist edited field values → FormService merges into manualOverrides, regenerates edit + view copies.
  formsApplyOverrides: (packageId, instanceId) => `${API_BASE_URL}/api/v1/forms/packages/${packageId}/forms/${instanceId}/overrides`,
  addDocumentForm: (transactionId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/form`,
  syncDocuSignRoom: (transactionId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/sync-to-room`,
  uploadDocument: (transactionId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/upload`,
  downloadDocument: (transactionId, documentId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/${documentId}/download`,
  documentFillSession: (transactionId, documentId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/${documentId}/fill-session`,
  createEnvelope: (transactionId, documentId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/${documentId}/envelope`,
  createEnvelopeSenderView: (transactionId, documentId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/${documentId}/envelope-sender-view`,
  refreshSenderView: (envelopeId) => `${API_BASE_URL}/api/envelopes/${envelopeId}/sender-view`,
  // Colorado forms: create a DocuSign draft envelope from an inline base64 PDF — NO DocuSign Room.
  envelopeDraftFromPdf: `${API_BASE_URL}/api/envelopes/draft-from-pdf`,
  // Record the envelope id on the form instance + lock it (status → SENT).
  formsMarkSent: (packageId, instanceId) => `${API_BASE_URL}/api/v1/forms/packages/${packageId}/forms/${instanceId}/envelope`,
  // Colorado forms: one-call send-for-signature with signature/date tabs placed automatically
  // (from co-form-tabs/{formId}_tabs.csv) — no DocuSign Sender View tagging step. Returns 422 if
  // the form has no tab-position data yet, or none of the supplied signers matched a role on the
  // form; callers should fall back to the manual draft-from-pdf + Sender View flow in that case.
  coloradoAutoSendForSignature: (packageId, instanceId) => `${API_BASE_URL}/api/v1/docusign/packages/${packageId}/forms/${instanceId}/send-for-signature`,
  envelopeStatus: (envelopeId) => `${API_BASE_URL}/api/envelopes/${envelopeId}`,
  envelopeRecipients: (envelopeId) => `${API_BASE_URL}/api/envelopes/${envelopeId}/recipients`,
  envelopeDocuments: (envelopeId) => `${API_BASE_URL}/api/envelopes/${envelopeId}/documents`,
  interestedProperties: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/interested-properties`,
  checkInterestedProperty: (transactionId, mlsId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/interested-properties/check/${mlsId}`,
  deleteInterestedProperty: (transactionId, mlsId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/interested-properties/${mlsId}`,
  markRpaCreated: (transactionId, propertyId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/interested-properties/${propertyId}/rpa-created`,
  updatePropertyDetails: (transactionId) => `${API_BASE_URL}/api/v1/internal/transactions/${transactionId}/buyerUpdatePropertyDetails`,
  mlsSearch: `${API_BASE_URL}/api/v1/mls/search`,
  attomAddressLookup: `${API_BASE_URL}/api/v1/mls/attom/address-lookup`,
  // Favorites — server-persisted against the caller's account, same endpoints
  // serve Agent / Broker / Buyer / Seller (mirrors mobile property.service.ts)
  favoriteProperty: (propertyId) => `${API_BASE_URL}/api/v1/properties/${encodeURIComponent(propertyId)}/favorite`,
  favorites: `${API_BASE_URL}/api/v1/properties/favorites`,
  favoritesIds: `${API_BASE_URL}/api/v1/properties/favorites/ids`,
  // CMA (Competitive Market Analysis) reports — mirrors mobile cma.service.ts,
  // routed through the API Gateway to rialty-analysis-service (/api/v1/cma/**)
  cma: `${API_BASE_URL}/api/v1/cma/json`, // generate — pass { address, transactionId } as params
  cmaHistory: `${API_BASE_URL}/api/v1/cma/history`, // pass { address } as params
  cmaDraft: `${API_BASE_URL}/api/v1/cma/draft`, // POST { address, transactionId } -> comp review draft
  cmaDraftById: (draftId) => `${API_BASE_URL}/api/v1/cma/draft/${encodeURIComponent(draftId)}`,
  cmaDraftGenerate: (draftId) => `${API_BASE_URL}/api/v1/cma/draft/${encodeURIComponent(draftId)}/generate`,
  cmaReportById: (reportId) => `${API_BASE_URL}/api/v1/cma/${encodeURIComponent(reportId)}`,
  cmaReportPdf: (reportId) => `${API_BASE_URL}/api/v1/cma/${encodeURIComponent(reportId)}/pdf`,
  selectInterestedProperty: (transactionId, propertyId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/interested-properties/${propertyId}/select`,
  // Chat endpoints
  createChat: `${API_BASE_URL}/api/v1/chats`,
  chatById: (chatId) => `${API_BASE_URL}/api/v1/chats/${chatId}`,
  chatMessages: (chatId) => `${API_BASE_URL}/api/v1/chats/${chatId}/messages`,
  chatParticipants: (chatId) => `${API_BASE_URL}/api/v1/chats/${chatId}/participants`,
  chatDelivered: (chatId) => `${API_BASE_URL}/api/v1/chats/${chatId}/delivered`,
  chatRead: (chatId) => `${API_BASE_URL}/api/v1/chats/${chatId}/read`,
  chatAttachments: (chatId) => `${API_BASE_URL}/api/v1/chats/${chatId}/attachments`,
  chatHide: (chatId) => `${API_BASE_URL}/api/v1/chats/${chatId}/hide`,
  // Users by role
  usersByRole: (role) => `${API_BASE_URL}/api/v1/users/by-role/${role}`,
  // Caller's contact graph — users with a direct relationship (accepted invite,
  // shareable-link acceptance, or shared transaction), already intersected with
  // the chat role visibility matrix server-side. Mirrors mobile's
  // contactService.getContacts() / API_ENDPOINTS.USERS.CONTACTS.
  usersContacts: `${API_BASE_URL}/api/v1/users/me/contacts`,
  // Invitations
  createInvitation: `${API_BASE_URL}/api/v1/invitations`,
  resendInvitation: `${API_BASE_URL}/api/v1/invitations/resend`,
  validateInvitation: (key) => `${API_BASE_URL}/api/v1/invitations/validate/${key}`,
  acceptUserInvite: (key) => `${API_BASE_URL}/api/v1/invitations/${key}/accept`,
  sendInviteEmail: `${API_BASE_URL}/api/v1/email/invite/send`,
  // Calendar entries
  calendarEntries: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/calendar-entries`,
  deleteCalendarEntry: (transactionId, entryId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/calendar-entries/${entryId}`,

  // CRM (rialty-crm-service). book is 'personal' or 'brokerage'.
  crmBooks: `${CRM_API_BASE_URL}/api/v1/crm/books`,
  crmPersons: (book) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/persons`,
  crmPerson: (book, id) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/persons/${id}`,
  crmPipeline: (book) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/pipeline`,
  crmToday: (book) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/today`,
  crmLead: (book, id, side) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/persons/${id}/leads/${side}`,
  crmActivities: (book, id) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/persons/${id}/activities`,
  crmTasks: (book, id) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/persons/${id}/tasks`,
  crmTask: (book, id, taskId) => `${CRM_API_BASE_URL}/api/v1/crm/${book}/persons/${id}/tasks/${taskId}`,
  // Milestone actions
  createMilestone: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/milestones`,
  completeMilestone: (transactionId, milestoneId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/milestones/${milestoneId}/complete`,
  skipMilestone: (transactionId, milestoneId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/milestones/${milestoneId}/skip`,
  deleteMilestone: (transactionId, milestoneId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/milestones/${milestoneId}`,
  updateMilestone: (transactionId, milestoneId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/milestones/${milestoneId}`,
  // Transaction status
  changeTransactionStatus: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/status`,
  // Prospect → Active conversion (BUYING side): backend fetches RPA from DocuSign and AI-parses it
  parseActiveTimeline: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/parse-active-timeline`,
  // Persist AI-parsed timeline, parties, and propertyAddress, then flip status → ACTIVE
  convertToActive: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/convert-to-active`,
  // Backfill the acceptance anchor (post-convert) + recompute dueDate on every
  // milestone carrying metadata.anchorPending. See SetAcceptanceDateRequest.
  setTransactionAcceptanceDate: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/acceptance-date`,
  // Active Listing 5-stage checklist (LISTING side): template + kickoff
  activeListingStages: `${API_BASE_URL}/api/v1/prospects/active-listing-stages`,
  startActiveListing: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/start-active-listing`,
  // Active Buying stage overlay (BUYING side) — state-aware: TX returns 14
  // stages, DEFAULT (any state outside CA/CO/TX) returns 13 stages, CA/CO
  // return an empty list (they stay milestone-driven, no overlay).
  activeBuyingStages: `${API_BASE_URL}/api/v1/prospects/active-buying-stages`,
  // DocuSign OIDC
  docusignConnect: `${API_BASE_URL}/api/v1/docusign/oidc/connect`,
  docusignDisconnect: `${API_BASE_URL}/api/v1/docusign/oidc/connect`,
  docusignCallback: `${API_BASE_URL}/api/v1/docusign/oidc/callback`,
  docusignOidcStatus: `${API_BASE_URL}/api/v1/docusign/oidc/status`,
  // Re-trigger the DocuSign Rooms company invite — used when connected but no
  // invite exists yet (auto-invite on OIDC callback silently failed). Mirrors
  // mobile's docusignService.sendInvite() / DOCUSIGN.OIDC_INVITE.
  docusignSendInvite: `${API_BASE_URL}/api/v1/docusign/oidc/invite`,
  // DocuSign Connected Services
  docusignRoomsUsers: `${API_BASE_URL}/api/rooms/company/users`,
  docusignRoomsReinvite: (userId) => `${API_BASE_URL}/api/rooms/company/reinvite/${userId}`,
  docusignFormProviderSetupUrl: `${API_BASE_URL}/api/v1/docusign/form-providers/setup-url`,
  docusignFormProviderStatus: `${API_BASE_URL}/api/v1/docusign/form-providers/status`,
  // Ria AI chat + document parsing
  riaChat: `${RIA_API_BASE_URL}/api/v1/ria/chat`,
  riaConversations: `${RIA_API_BASE_URL}/api/v1/ria/conversations`,
  riaConversationById: (convId) => `${RIA_API_BASE_URL}/api/v1/ria/conversations/${convId}`,
  riaParseDocuments: `${API_BASE_URL}/api/v1/ria/transactions/parse-documents`,
  // Notifications
  // (Notification preferences are stored under user profile — see
  // API_ENDPOINTS.userProfile(userId) and NotificationSettingsPage.)
  notifications: `${API_BASE_URL}/api/v1/notifications`,
  notificationsUnreadCount: `${API_BASE_URL}/api/v1/notifications/unread-count`,
  markNotificationRead: (id) => `${API_BASE_URL}/api/v1/notifications/${id}/read`,
  markAllNotificationsRead: `${API_BASE_URL}/api/v1/notifications/read-all`,
  fcmToken: `${API_BASE_URL}/api/v1/notifications/fcm-token`,
  // Password reset
  sendResetCode: `${API_BASE_URL}/api/v1/auth/password/reset-code`,
  verifyResetCode: `${API_BASE_URL}/api/v1/auth/password/verify-code`,
  resetPassword: `${API_BASE_URL}/api/v1/auth/password/reset`,
  // RPA Wizard
  rpaWizardDrafts: (txnId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/rpa-wizard`,
  rpaWizardDraftById: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/rpa-wizard/${draftId}`,
  rpaWizardSaveStep: (txnId, draftId, stepNumber, completed) => `${API_BASE_URL}/api/v1/transactions/${txnId}/rpa-wizard/${draftId}/steps/${stepNumber}?completed=${completed}`,
  rpaWizardSubmit: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/rpa-wizard/${draftId}/submit`,
  rpaWizardReopen: (txnId, interestedPropertyId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/rpa-wizard/reopen?interestedPropertyId=${interestedPropertyId}`,
  // Broker team management
  brokerAgents: `${API_BASE_URL}/api/v1/broker/agents`,
  brokerAgentCount: `${API_BASE_URL}/api/v1/broker/agents/count`,
  brokerAgentIds: `${API_BASE_URL}/api/v1/broker/agents/ids`,
  brokerTransactionCounts: `${API_BASE_URL}/api/v1/broker/transactions/counts`,
  brokerTransactionOwnerIds: `${API_BASE_URL}/api/v1/broker/transaction-owner-ids`,
  brokerManagerAgents: `${API_BASE_URL}/api/v1/broker-manager/agents`,
  brokerAdminAgents: `${API_BASE_URL}/api/v1/broker-admin/agents`,
  // Broker license verification
  verifyBroker: `${API_BASE_URL}/api/public/verify-broker`,
  checkBrokerLicense: `${API_BASE_URL}/api/v1/users/check-broker-license`,
  // Designated Broker email check — public, no auth (mirrors mobile BrokerageVerificationScreen)
  checkDesignatedBroker: `${API_BASE_URL}/api/v1/users/check-designated-broker`,
  // Brokerage → linked brokers (Designated Brokers)
  brokerageLinkedBrokers: `${API_BASE_URL}/api/v1/brokerage/brokers`,
  brokerageLinkedBrokerCount: `${API_BASE_URL}/api/v1/brokerage/brokers/count`,
  brokerageLinkedBrokerIds: `${API_BASE_URL}/api/v1/brokerage/brokers/ids`,
  brokerageIsBrokerLinked: (brokerUserId) => `${API_BASE_URL}/api/v1/brokerage/brokers/${brokerUserId}/linked`,
  // Brokerage → linked members (brokers + agents reachable through them, deduped)
  brokerageLinkedMemberIds: `${API_BASE_URL}/api/v1/brokerage/members/ids`,
  brokerageLinkedMemberCount: `${API_BASE_URL}/api/v1/brokerage/members/count`,
  // Service Provider (Company) → linked individual vendors ("Broadcast to Vendors" on /conversations/sp)
  spCompanyVendorCount: `${API_BASE_URL}/api/v1/service-provider/company/vendors/count`,
  spCompanyVendorIds: `${API_BASE_URL}/api/v1/service-provider/company/vendors/ids`,
  // Brokerage firm profile
  brokerageTransactions: `${API_BASE_URL}/api/v1/brokerage/transactions`,
  brokerageProfile: `${API_BASE_URL}/api/v1/brokerage/profile`,
  // Buyer Offer Submission
  submitOffer: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offer/submit`,
  savePreApproval: (transactionId, propertyId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/interested-properties/${propertyId}/pre-approval`,
  // Seller Offers
  transactionOffers: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers`,
  transactionOffersUpload: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/upload`,
  transactionOfferById: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}`,
  transactionOfferStatus: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/status`,
  transactionOfferInterested: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/interested`,
  sendOfferForSignature: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/send-for-signature`,
  markOfferSent: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/mark-sent`,
  markOfferFinal: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/mark-final`,
  // Per-offer seller-visibility grant (user ask 2026-10-29). Agent/TC only;
  // backend returns 401 for seller callers.
  shareOfferWithSeller: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/share-with-seller`,
  unshareOfferWithSeller: (transactionId, offerId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/unshare-with-seller`,
  transactionOffersMerged: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/merged`,
  transactionOffersVersionDiffs: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/version-diffs`,
  downloadOfferDocument: (transactionId, offerId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offers/${offerId}/documents/${documentId}/download`,
  transactionOfferLink: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offer-link`,
  transactionOfferLinkEnabled: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/offer-link/enabled`,
  // Prospect stage definitions
  prospectStages: `${API_BASE_URL}/api/v1/prospects/stages`,
  // TDS Wizard
  tdsWizardDrafts: (txnId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/tds-wizard`,
  tdsWizardDraftById: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/tds-wizard/${draftId}`,
  tdsWizardSaveStep: (txnId, draftId, stepNumber, completed) => `${API_BASE_URL}/api/v1/transactions/${txnId}/tds-wizard/${draftId}/steps/${stepNumber}?completed=${completed}`,
  tdsWizardSubmit: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/tds-wizard/${draftId}/submit`,
  tdsWizardLock: (txnId, interestedPropertyId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/tds-wizard/lock?interestedPropertyId=${interestedPropertyId}`,
  tdsWizardReopen: (txnId, interestedPropertyId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/tds-wizard/reopen?interestedPropertyId=${interestedPropertyId}`,
  // SPQ Wizard
  spqWizardDrafts: (txnId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spq-wizard`,
  spqWizardDraftById: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spq-wizard/${draftId}`,
  spqWizardSaveStep: (txnId, draftId, stepNumber, completed) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spq-wizard/${draftId}/steps/${stepNumber}?completed=${completed}`,
  spqWizardSubmit: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spq-wizard/${draftId}/submit`,
  spqWizardLock: (txnId, interestedPropertyId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spq-wizard/lock?interestedPropertyId=${interestedPropertyId}`,
  spqWizardReopen: (txnId, interestedPropertyId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spq-wizard/reopen?interestedPropertyId=${interestedPropertyId}`,
  // CBS Wizard (CO only — Contract to Buy & Sell Real Estate)
  cbsWizardDrafts: (txnId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/cbs-wizard`,
  cbsWizardDraftById: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/cbs-wizard/${draftId}`,
  cbsWizardSaveStep: (txnId, draftId, stepNumber, completed) => `${API_BASE_URL}/api/v1/transactions/${txnId}/cbs-wizard/${draftId}/steps/${stepNumber}?completed=${completed}`,
  cbsWizardSubmit: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/cbs-wizard/${draftId}/submit`,
  cbsWizardReopen: (txnId, interestedPropertyId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/cbs-wizard/reopen?interestedPropertyId=${interestedPropertyId}`,
  // SPD Wizard (CO only — Seller's Property Disclosure SPD19)
  spdWizardDrafts: (txnId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spd-wizard`,
  spdWizardDraftById: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spd-wizard/${draftId}`,
  spdWizardSaveStep: (txnId, draftId, stepNumber, completed) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spd-wizard/${draftId}/steps/${stepNumber}?completed=${completed}`,
  spdWizardSubmit: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spd-wizard/${draftId}/submit`,
  spdWizardReopen: (txnId, draftId) => `${API_BASE_URL}/api/v1/transactions/${txnId}/spd-wizard/${draftId}/reopen`,
  // Security Settings
  securityStatus: `${API_BASE_URL}/api/v1/users/me/security-status`,
  verifyEmailSend: `${API_BASE_URL}/api/v1/users/me/verify-email/send`,
  verifyEmailConfirm: `${API_BASE_URL}/api/v1/users/me/verify-email/confirm`,
  verifyPhoneSend: `${API_BASE_URL}/api/v1/users/me/verify-phone/send`,
  verifyPhoneConfirm: `${API_BASE_URL}/api/v1/users/me/verify-phone/confirm`,
  mfaEnable: `${API_BASE_URL}/api/v1/users/me/mfa/enable`,
  mfaDisable: `${API_BASE_URL}/api/v1/users/me/mfa/disable`,
  // Trusted Devices
  trustedDevices: `${API_BASE_URL}/api/v1/users/me/trusted-devices`,
  trustedDeviceRevoke: (deviceId) => `${API_BASE_URL}/api/v1/users/me/trusted-devices/${deviceId}`,
  trustedDeviceCheck: `${API_BASE_URL}/api/v1/users/me/trusted-devices/check`,
  trustedDeviceTrust: `${API_BASE_URL}/api/v1/users/me/trusted-devices`,
  trustedDeviceLogin: `${API_BASE_URL}/api/v1/auth/trusted-device-login`,
  // WebAuthn / Passkeys (Sign in with Fingerprint)
  webauthnRegisterOptions: `${API_BASE_URL}/api/v1/auth/webauthn/register-options`,
  webauthnRegisterVerify: `${API_BASE_URL}/api/v1/auth/webauthn/register-verify`,
  webauthnLoginOptions: `${API_BASE_URL}/api/v1/auth/webauthn/login-options`,
  webauthnLoginVerify: `${API_BASE_URL}/api/v1/auth/webauthn/login-verify`,
  webauthnCredentials: `${API_BASE_URL}/api/v1/auth/webauthn/credentials`,
  webauthnCredentialById: (id) => `${API_BASE_URL}/api/v1/auth/webauthn/credentials/${id}`,
  // Payments
  paymentMethods: `${API_BASE_URL}/api/v1/payments/methods`,
  paymentMethodById: (id) => `${API_BASE_URL}/api/v1/payments/methods/${id}`,
  setDefaultPaymentMethod: (id) => `${API_BASE_URL}/api/v1/payments/methods/${id}/default`,
  paymentCharges: `${API_BASE_URL}/api/v1/payments/charges`,
  paymentChargeByTransaction: (transactionId) => `${API_BASE_URL}/api/v1/payments/charges/transaction/${transactionId}`,
  retryPaymentCharge: (paymentIntentId) => `${API_BASE_URL}/api/v1/payments/charges/${paymentIntentId}/retry`,
  paymentSheetIntent: `${API_BASE_URL}/api/v1/payments/charges/payment-sheet`,
  // Web embedded Stripe Checkout — returns {clientSecret, sessionId} that the
  // frontend passes to <EmbeddedCheckoutProvider>. Replaces the old
  // server-confirmed `paymentCharges` POST for the Pay-Now flow.
  paymentCheckoutSession: `${API_BASE_URL}/api/v1/payments/charges/checkout-session`,
  paymentSummary: `${API_BASE_URL}/api/v1/payments/summary`,
  // GCS Document Upload (non-DocuSign)
  transactionFiles: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files`,
  deleteTransactionFile: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}`,
  downloadTransactionFile: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}/download`,
  shareTransactionFile: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}/share`,
  uploadTransactionFile: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/upload`,
  // Transaction Documents — Folders (mirrors mobile document.service.ts)
  transactionFolders: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders`, // GET (list: string[]) / POST body {folder}
  renameTransactionFolder: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/${folder}`, // PATCH body {newFolder}
  deleteTransactionFolder: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/${folder}`, // DELETE — folder + everything in it
  downloadTransactionFolder: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/download/${folder}`, // GET — folder (+ subfolders) as a ZIP
  updateTransactionFile: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}`, // PATCH body subset of {fileName, folder}
  // Per-folder cover-sheet metadata + preview (literal-before-{*folder} path shape mirrors
  // downloadTransactionFolder — Spring routes catch-all captures only in terminal position).
  folderMetadata: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/metadata/${folder}`, // GET
  folderInstructions: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/instructions/${folder}`, // PATCH body {instructions}
  folderCoverSheetVisibility: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/cover-sheet-visibility/${folder}`, // PATCH body {hidden}
  folderCoverSheetContent: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/cover-sheet-content/${folder}`, // GET JSON (powers HTML preview)
  folderCombinedPdf: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/combined-pdf/${folder}`, // GET application/pdf (cover sheet + merged docs)
  folderCoverSheetPdf: (transactionId, folder) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/folders/cover-sheet-pdf/${folder}`, // GET application/pdf
  // Cover-sheet property photo — transaction-scoped (not folder-scoped) because the
  // property is the same regardless of which folder's cover sheet is being generated.
  // PUT multipart (field "file") to upload/replace, DELETE to remove, GET returns
  // image/jpeg (404 when none set — the preview page uses that fingerprint to render
  // the photo-less layout rather than an empty placeholder).
  coverSheetPhoto: (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/cover-sheet-photo`,
  updateTransactionFileOrder: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}/order`, // PATCH body {displayOrder}
  // GCS document Send for Signature — PDF-only, Room-free (creates envelope directly from the file's bytes)
  sendTransactionFileForSignature: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}/send-for-signature`,
  refreshTransactionFileSenderView: (transactionId, documentId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/files/${documentId}/sender-view`,
  // Inbound Email
  inboundEmailByTransaction: (transactionId) => `${API_BASE_URL}/api/v1/inbound-email/by-transaction/${transactionId}`,
  inboundEmailAttachmentDownload: (emailId, attachmentIndex) => `${API_BASE_URL}/api/v1/inbound-email/${emailId}/attachments/${attachmentIndex}/download`,
  // Outbound Email — delivery-tracked transactional sends (notification-service)
  outboundEmailByTransaction: (transactionId) => `${API_BASE_URL}/api/v1/outbound-email/by-transaction/${transactionId}`,
  outboundEmailById: (id) => `${API_BASE_URL}/api/v1/outbound-email/${id}`,
  // Contact
  contact: `${API_BASE_URL}/api/v1/contact`,
  // Activity Log
  activityLogs: `${API_BASE_URL}/api/v1/activity-logs`,
  activityLogStats: `${API_BASE_URL}/api/v1/activity-logs/stats`,
  activityLogTransactions: `${API_BASE_URL}/api/v1/activity-logs/transactions`,
  // Auth lifecycle
  loginCompleted: `${API_BASE_URL}/api/v1/auth/login-completed`,
  // Personal audit feed
  auditUserEvents: `${API_BASE_URL}/api/v1/audit/users/me/events`,
  // Transaction-scoped audit feed (History tab)
  transactionAuditEvents: (transactionId) => `${API_BASE_URL}/api/v1/audit/resources/transactions/${transactionId}/events`,
  // Service Requests
  serviceProviders: `${API_BASE_URL}/api/v1/users/service-providers`,
  // Canonical service-type catalog — mirrors mobile's
  // serviceProviderService.getServiceTypes() / API_ENDPOINTS.SERVICE_PROVIDERS.SERVICE_TYPES.
  // Response items are { id: number, label: string }; label is both the display
  // text and the wire value expected on writes (ServiceProviderProfile.providerType).
  serviceTypes: `${API_BASE_URL}/api/v1/service-types`,
  createServiceRequest: `${API_BASE_URL}/api/v1/create-service-request`,
  serviceRequestsByAgent: (agentId) => `${API_BASE_URL}/api/v1/service-requests/agent/${agentId}`,
  serviceRequestsByTransaction: (txnId) => `${API_BASE_URL}/api/v1/service-requests/transaction/${txnId}`,
  // Reassign / reschedule a still-PENDING sent service request — mirrors mobile
  // PATCH /api/v1/users/me/sent-service-requests/{id}.
  patchSentServiceRequest: (requestId) => `${API_BASE_URL}/api/v1/users/me/sent-service-requests/${requestId}`,
  // Requester-scope cancel — POST /api/v1/users/me/sent-service-requests/{id}/cancel.
  // Server soft-transitions the SR to CANCELLED and fires SR_CANCELLED to the
  // provider. Used as a client-side safety net after deleting a calendar event
  // whose server-side back-link may have been missing; 400 = cascade already
  // handled it.
  cancelSentServiceRequest: (requestId) => `${API_BASE_URL}/api/v1/users/me/sent-service-requests/${requestId}/cancel`,
  // Fresh single-SR fetch for the requester's details modal — the sentSrs
  // list cache refreshes on tab-focus only, but billedAmount +
  // finalPaymentAmount mutate mid-flight so the modal calls this on every
  // open to render current values.
  sentServiceRequestById: (requestId) => `${API_BASE_URL}/api/v1/users/me/sent-service-requests/${requestId}`,
  // List service requests the authenticated agent has sent, optionally
  // narrowed to one transaction — mirrors mobile's listMySentServiceRequests
  // (GET /api/v1/users/me/sent-service-requests?transactionId=...). This is
  // the correct agent-scoped read; serviceRequestsByTransaction above hits a
  // different, unrelated service and 403s for the agent role.
  sentServiceRequests: (transactionId) => `${API_BASE_URL}/api/v1/users/me/sent-service-requests${transactionId ? `?transactionId=${transactionId}` : ''}`,
  // Transaction Coordinator — brokerage-linked agents (Primary Agent picker),
  // mirroring mobile TC parity work. Brokerage licence verification on
  // TCRegistrationScreen reuses the existing verifyLicense endpoint above
  // (role: 'BROKERAGE'), same as mobile's VERIFICATION.LICENSE.
  tcAgents: `${API_BASE_URL}/api/v1/tc/agents`,
  // TC's affiliated brokerage, joined via the licence #/state on their UserProfile
  // (mirrors mobile tc.service.ts getMyBrokerage — GET /api/v1/tc/brokerage).
  tcBrokerage: `${API_BASE_URL}/api/v1/tc/brokerage`,
  // Delete a DocuSign document — formalizes what handleDeleteDocument previously
  // built via ad-hoc string concatenation.
  deleteDocusignDocument: (transactionId, documentId) => `${API_BASE_URL}/api/v1/docusign/transactions/${transactionId}/documents/${documentId}`,
  // SP profile — self-service combined profile (SpCombinedProfileResponse),
  // same contract the SP mobile app targets via authAPI.getProfile/updateProfile.
  spProfile:      `${API_BASE_URL}/api/v1/users/me/sp-profile`,
  spProfilePhoto: `${API_BASE_URL}/api/v1/users/me/sp-profile/photo`,
  spProfileCompanyLogo: `${API_BASE_URL}/api/v1/users/me/sp-profile/company-logo`,
  // Picker-backed search over COMPANY-vendorType SPs for the Individual SP's
  // "Find my company" affordance on SpProfilePage. Thin passthrough to
  // user-service `GET /api/v1/service-provider/company/search?q=&limit=`.
  // Response rows are {userId, businessName, companyLogoUrl, city, state, email, phone}.
  spCompanySearch: `${API_BASE_URL}/api/v1/service-provider/company/search`,
  // SP Jobs — same contract the SP mobile app's serviceRequestAPI targets
  // (rialty-user-service via the gateway's /api/v1/users/** route).
  // /api/v1/service-providers/** and /api/v1/service-requests/** are legacy
  // aliases that route to a different, unrelated service — not used here.
  spServices: (status) => `${API_BASE_URL}/api/v1/users/me/service-requests?status=${status}`,
  // Job detail — response is the full service-request object with the 6-step
  // timeline embedded as a `timeline` array field, not a dedicated sub-resource.
  serviceRequestTimeline: (serviceId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${serviceId}`,
  updateServiceStatus: (serviceId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${serviceId}/status`,
  uploadServiceReport: (requestId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${requestId}/report`,
  deleteServiceReport: (requestId, attachmentId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${requestId}/report/${attachmentId}`,
  // Broaden a report attachment's visibility. Uploader-only server-side;
  // additive (never revokes); uploader's own audience is stripped
  // silently so it's safe to POST the full audience set from the client.
  shareServiceAttachment: (requestId, attachmentId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${requestId}/report/${attachmentId}/share`,
  // Requester-side "Mark as Transaction Document" (spec §5). Promotes a shared
  // report attachment into the transaction's Docs tab under folder=service-request.
  // Idempotent server-side: re-filing returns the same TransactionDocument id
  // without creating a duplicate row.
  fileServiceAttachmentToTransaction: (requestId, attachmentId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${requestId}/report/${attachmentId}/file-to-transaction`,
  // Inspection-report Checklist — generate/view/edit. generateChecklist and
  // getChecklist both return the same ChecklistResponse shape (status +
  // items), so "Generate Checklist" and "View Generated" render through the
  // same popup component regardless of which call populated it.
  generateChecklist: (requestId, attachmentId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${requestId}/report/${attachmentId}/checklist/generate`,
  getChecklist: (requestId, attachmentId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${requestId}/report/${attachmentId}/checklist`,
  updateChecklistItemPriority: (requestId, attachmentId, itemId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${requestId}/report/${attachmentId}/checklist/items/${itemId}/priority`,
  // Company-admin action: route an AWAITING_ASSIGNMENT job to one of the
  // company's on-staff inspectors (Jobs → Requests "Assign" card action).
  assignInspector: (serviceId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${serviceId}/assign-inspector`,
  // Company-admin action: move a still-PENDING job (already routed to one
  // on-staff inspector who hasn't accepted yet) to a different on-staff
  // inspector (Jobs → Active "Re-Assign" card action).
  reassignInspector: (serviceId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${serviceId}/reassign-inspector`,
  // Company-admin action: decline the SR outright — no inspector routing at
  // all. Terminal DECLINE; backend fires SR_COMPANY_DECLINED to the requester
  // with the company's name in the copy (distinct from inspector-decline).
  // Callable on AWAITING_ASSIGNMENT + company-routed PENDING rows.
  companyDeclineRequest: (serviceId) => `${API_BASE_URL}/api/v1/users/me/service-requests/${serviceId}/company-decline`,
  // Roster (id, name, service type, etc.) of individual vendors linked to the
  // current Company SP — populates the Assign modal's provider list. Distinct
  // from spCompanyVendorCount/spCompanyVendorIds above (those back the
  // "Broadcast to Vendors" chat feature and don't include names).
  companyVendors: `${API_BASE_URL}/api/v1/service-provider/company/vendors`,
  // Jobs currently held by the Company's on-staff roster (query-time join on
  // the backend — assign-inspector moves providerId off the company, so the
  // plain spServices(status) call above stops finding a routed job). Used by
  // JobsPage to keep a routed-and-accepted job visible to the Company too.
  companyJobs: (status) => `${API_BASE_URL}/api/v1/service-provider/company/jobs?status=${status}`,
};
